import useModuleFilters from '../../../shared/data/useModuleFilters';
import FilterBar from '../../../shared/components/patterns/FilterBar';
import { assetFormSchema } from '../adminFilter.schemas';
import { useEffect, useMemo, useState } from 'react';
import { listAdminAssets, uploadAdminAsset } from '@/modules/admin/services/admin-assets.service';
import { listAdminTenants } from '@/modules/admin/services/admin-tenants.service';

const PLATFORM_UPLOADS = [
  { label: 'Logo piattaforma', usageKey: 'platform_logo', assignmentKey: 'platform.logo' },
  { label: 'Mark piattaforma', usageKey: 'platform_mark', assignmentKey: 'platform.mark' },
  { label: 'Favicon piattaforma', usageKey: 'platform_favicon', assignmentKey: 'platform.favicon' },
];

const TEMPLATE_OPTIONS = [
  { label: 'fattura', usageKey: 'tenant_invoice_template', assignmentKey: 'tenant.templates.invoice' },
  { label: 'contratto', usageKey: 'tenant_contract_template', assignmentKey: 'tenant.templates.contract' },
  { label: 'generico', usageKey: 'tenant_generic_document_template', assignmentKey: 'tenant.templates.generic' },
];

export default function AdminAssetsPage() {
  const [tenants, setTenants] = useState([]);
  const [assets, setAssets] = useState([]);
  const [tenantId, setTenantId] = useState('');
  const [templateType, setTemplateType] = useState(TEMPLATE_OPTIONS[0].usageKey);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const tenantTemplates = useMemo(
    () => assets.filter((asset) => asset.scope === 'TENANT_TEMPLATE'),
    [assets]
  );

  useEffect(() => {
    let active = true;
    async function load() {
      const [tenantsResult, assetsResult] = await Promise.all([
        listAdminTenants(),
        listAdminAssets(),
      ]);
      if (!active) return;
      if (tenantsResult.ok) {
        const realTenants = tenantsResult.data.tenants.filter((tenant) => !tenant.isPlatformRoot);
        setTenants(realTenants);
        setTenantId((currentTenantId) => currentTenantId || realTenants[0]?.id || '');
      }
      if (assetsResult.ok) {
        setAssets(assetsResult.data.assets);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, []);

  async function refreshAssets() {
    const result = await listAdminAssets();
    if (result.ok) {
      setAssets(result.data.assets);
    }
  }

  async function handleUpload(event, options) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    setMessage('');
    const result = await uploadAdminAsset({
      file,
      scope: options.scope,
      tenantId: options.tenantId,
      usageKey: options.usageKey,
      displayName: options.displayName,
      autoAssign: true,
    });
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setMessage(result.message);
    await refreshAssets();
  }

  const selectedTemplate = TEMPLATE_OPTIONS.find((option) => option.usageKey === templateType) || TEMPLATE_OPTIONS[0];

  const moduleFilters = useModuleFilters({ schema: assetFormSchema, rows: tenantTemplates });
  const filteredRows = moduleFilters.filterRows(tenantTemplates);

  return (
    <section className="control-plane-page">
      <header className="control-plane-topbar">
        <div>
          <div className="control-plane-topbar__title">Asset</div>
          <div className="control-plane-topbar__subtitle">Brand piattaforma, brand tenant e template iniziali.</div>
        </div>
      </header>
        <FilterBar model={moduleFilters} />


      <div className="control-plane-content">
        {message ? <div className="control-plane-info-panel">{message}</div> : null}
        {error ? <div role="alert" className="control-plane-inline-error wt-inline-error">{error}</div> : null}

        <section className="control-plane-card">
          <div className="control-plane-card__header">
            <div>
              <div className="control-plane-card__title">Brand piattaforma</div>
              <div className="control-plane-card__subtitle">Logo, mark e favicon globali Webtika.</div>
            </div>
          </div>
          <div className="control-plane-form-row control-plane-form-row--3">
            {PLATFORM_UPLOADS.map((upload) => (
              <label key={upload.usageKey} className="control-plane-field">
                <span>{upload.label}</span>
                <input
                  type="file"
                  accept="image/svg+xml,image/png,image/webp,image/jpeg"
                  onChange={(event) => handleUpload(event, {
                    scope: 'PLATFORM_BRAND',
                    usageKey: upload.usageKey,
                    displayName: upload.label,
                  })}
                />
              </label>
            ))}
          </div>
        </section>

        <section className="control-plane-card">
          <div className="control-plane-card__header">
            <div>
              <div className="control-plane-card__title">Brand tenant</div>
              <div className="control-plane-card__subtitle">Logo sidebar azienda cliente assegnato al tenant.</div>
            </div>
          </div>
          <div className="control-plane-form-row">
            <label className="control-plane-field">
              <span>Tenant</span>
              <select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                ))}
              </select>
            </label>
            <label className="control-plane-field">
              <span>Logo sidebar tenant</span>
              <input
                type="file"
                accept="image/svg+xml,image/png,image/webp,image/jpeg"
                onChange={(event) => handleUpload(event, {
                  scope: 'TENANT_BRAND',
                  tenantId,
                  usageKey: 'tenant_sidebar_logo',
                  displayName: 'Logo sidebar tenant',
                })}
              />
            </label>
          </div>
        </section>

        <section className="control-plane-card">
          <div className="control-plane-card__header">
            <div>
              <div className="control-plane-card__title">Template documentali tenant</div>
              <div className="control-plane-card__subtitle">File iniziali indicizzati, senza generazione documenti.</div>
            </div>
          </div>
          <div className="control-plane-form-row control-plane-form-row--3">
            <label className="control-plane-field">
              <span>Tenant</span>
              <select value={tenantId} onChange={(event) => setTenantId(event.target.value)}>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
                ))}
              </select>
            </label>
            <label className="control-plane-field">
              <span>Tipo template</span>
              <select value={templateType} onChange={(event) => setTemplateType(event.target.value)}>
                {TEMPLATE_OPTIONS.map((option) => (
                  <option key={option.usageKey} value={option.usageKey}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="control-plane-field">
              <span>File template</span>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => handleUpload(event, {
                  scope: 'TENANT_TEMPLATE',
                  tenantId,
                  usageKey: selectedTemplate.usageKey,
                  displayName: `Template ${selectedTemplate.label}`,
                })}
              />
            </label>
          </div>
          <div className="control-plane-associated-list" style={{ marginTop: '16px' }}>
            {filteredRows.map((asset) => (
              <article key={asset.id} className="control-plane-associated-item">
                <div className="control-plane-associated-item__title">{asset.displayName}</div>
                <div className="control-plane-associated-item__meta">{asset.tenantId} · {asset.usageKey} · {asset.originalFilename}</div>
              </article>
            ))}
            {tenantTemplates.length === 0 ? <div className="control-plane-empty-state">Nessun template caricato.</div> : null}
          </div>
        </section>
      </div>
    </section>
  );
}
